// Open-redirect guard for client-side navigation (M8).
//
// Post-sign-in redirect targets (`callbackUrl`) arrive from the query string
// and must never be handed to `window.location.assign()` or react-router's
// `navigate()` without a same-origin check. A `?callbackUrl=https://evil.com`
// would otherwise carry the freshly-authenticated session off-origin.
//
// This mirrors the server-side `safeCallbackUrl` in
// apps/server/src/modules/auth/auth.controller.ts: resolve the raw value
// against the current origin, accept only same-origin results, and fall back
// to '/' for anything that parses to a different origin, a dangerous scheme,
// or fails to parse at all.

/**
 * Return a same-origin URL safe to navigate to, or '/' if the input is
 * cross-origin, uses a non-http(s) scheme, or is unparsable.
 *
 * Relative paths ("/clients", "/login?foo=bar") are returned as-is. Absolute
 * same-origin URLs are normalised to their pathname+search. Everything else
 * degrades to '/'.
 */
export function safeRedirect(raw: string | null | undefined): string {
    if (!raw) return '/';
    const value = raw.trim();
    if (!value) return '/';

    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://localhost';

    let target: URL;
    try {
        target = new URL(value, origin);
    } catch {
        return '/';
    }

    // Reject non-http(s) schemes (javascript:, data:, etc.) and any origin
    // that does not string-match the current page origin.
    if (target.origin !== origin) return '/';
    return target.pathname + target.search + target.hash;
}
